import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthService } from '@df/api';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';

const isDev = process.env.NODE_ENV === 'development';

function logDevError(context, error) {
  if (!isDev) return;
  console.error(context, error);
}

function extractErrorMessage(error) {
  if (isNetworkError(error)) {
    return 'Server unreachable. Is the backend running?';
  }
  return getSafeApiErrorMessage(error, 'Unexpected server response.');
}

const EmailVerificationPending = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEmail = location.state?.email || '';

  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleResend = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email to resend the verification link.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await AuthService.resendVerification(trimmedEmail);
      setSuccess('Verification email sent. Please check your inbox.');
    } catch (err) {
      logDevError('[VerifyPending] Resend failed', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" sx={{ color: '#2c5aa0', fontWeight: 'bold', mb: 1 }}>
            Check your email
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We sent a verification link to your email. Please verify to activate your account.
          </Typography>

          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={(evt) => setEmail(evt.target.value)}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={handleResend}
            disabled={loading}
            sx={{ mb: 2 }}
            fullWidth
          >
            {loading ? <CircularProgress size={20} /> : 'Resend Verification Email'}
          </Button>

          <Button variant="text" onClick={() => navigate('/login')} fullWidth>
            Back to Login
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default EmailVerificationPending;
